import {
  Button,
  Field,
  IconButton,
  IconTile,
  Input,
  Modal,
  TD,
  TH,
  Table,
  cx,
  Textarea,
  useToast,
} from '@ims/ui'
import { Receipt, Sparkles, Trash2 } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { parseSerials, setPurchaseOrderStatus, shelfLifeLabel } from '@ims/core'
import type { ProductRow, PurchaseOrderRow, RowOf } from '@ims/data'
import { LocationSelect, MoneyInput, ProductPicker, QtyInput, useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { clearDraft, draftKey, readDraft, saveDraft } from '../lib/drafts.ts'
import { money, qty, todayIso } from '../lib/format.ts'
import { followExpiry, shelfOf } from '../lib/batches.ts'
import { purchaseOrderRow } from '../lib/purchases.ts'
import {
  draftHasWork,
  draftTotal,
  parseReceiveDraft,
  restoreLines,
  type ReceiveDraft,
} from '../lib/receive-draft.ts'
import { receiveGoods, type SerialRow } from '../lib/rows.ts'
import { loadSerials, receiveProblem } from '../lib/serials.ts'
import { SupplierPicker } from '../party-picker.tsx'
import { useRepo, useWorkspace } from '../workspace.tsx'
import { finePointer } from '../lib/pointer.ts'
import { ScanButton, ScanSerialsButton } from '../scanner.tsx'
import { DraftBanner, DroppedNote } from './draft-banner.tsx'
import {
  BillFields,
  billDraftOps,
  billDraftProblem,
  emptyBillDraft,
  type BillDraft,
} from './supplier-bills.tsx'

// Taking a delivery into stock: from the Stock screen (anything, from anyone)
// or against a purchase order (what is still due on it, from its supplier).

interface ReceiveLine {
  key: string
  product: ProductRow
  qtyMilli: number | null
  costPaisa: number | null
  batchNo: string
  mfgDate: string
  expiryDate: string
  /** The expiry was worked out from the mfg date and shelf life (it follows the mfg date). */
  expiryAuto?: boolean
  mrpPaisa: number | null
  /** Serial-tracked products: one serial per line; the quantity is their count. */
  serialsText: string
  /** The purchase order line this fills. */
  orderLineId?: string
}

/** What is still due on a purchase order line, to start a receipt from. */
// Phones hide the table's header, so quantity and cost carry their names.
const PHONE_LABEL =
  'max-sm:before:mb-1 max-sm:before:block max-sm:before:text-xs max-sm:before:font-medium max-sm:before:text-muted'
const BATCH_LABEL = 'mb-1 flex items-center gap-1 text-xs font-medium text-muted'

export interface DueLine {
  lineId: string
  product: ProductRow
  qtyMilli: number
  costPaisa: number
}

export function ReceiveStockDialog({
  onClose,
  order,
  products,
}: {
  onClose: () => void
  /** Receive against this purchase order: its supplier, and its lines still due. */
  order?: { order: PurchaseOrderRow; due: DueLine[] }
  /**
   * Start with these on the lines, quantities to fill in (Receive on a list's low or out
   * row); the first one's usual supplier is chosen.
   */
  products?: readonly ProductRow[]
}) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const [supplierId, setSupplierId] = useState(
    order?.order.supplier_id ?? products?.[0]?.supplier_id ?? '',
  )
  const [locationId, setLocationId] = useState<string | null>(order?.order.location_id ?? null)
  const [reference, setReference] = useState('')
  const [lines, setLines] = useState<ReceiveLine[]>(() => {
    const blank = { batchNo: '', mfgDate: '', expiryDate: '', mrpPaisa: null, serialsText: '' }
    if (order)
      return order.due.map((d) => ({
        ...blank,
        key: crypto.randomUUID(),
        product: d.product,
        qtyMilli: d.product.track_serials ? null : d.qtyMilli,
        costPaisa: d.costPaisa,
        orderLineId: d.lineId,
      }))
    // Started from a list's row: those products, with their last cost, quantity to fill in.
    return (products ?? []).map((p) => ({
      ...blank,
      key: crypto.randomUUID(),
      product: p,
      qtyMilli: null,
      costPaisa: p.cost_price_paisa,
    }))
  })
  const [busy, setBusy] = useState(false)
  // The supplier's bill: its total follows the lines until it is typed.
  const [bill, setBill] = useState<BillDraft>(emptyBillDraft)
  const [billTyped, setBillTyped] = useState(false)

  // What was typed is kept on this device as it is typed (docs/plans/dialog-safety.md): one
  // draft for receiving without an order, one per purchase order.
  const keyOfDraft = current
    ? draftKey(current.tenant_id, 'receive', order?.order.id ?? 'free')
    : null
  const packed: ReceiveDraft = {
    supplierId,
    locationId,
    reference,
    bill,
    billTyped,
    lines: lines.map((l) => ({
      productId: l.product.id,
      qtyMilli: l.qtyMilli,
      costPaisa: l.costPaisa,
      batchNo: l.batchNo,
      mfgDate: l.mfgDate,
      expiryDate: l.expiryDate,
      expiryAuto: l.expiryAuto ?? false,
      mrpPaisa: l.mrpPaisa,
      serialsText: l.serialsText,
      orderLineId: l.orderLineId ?? null,
    })),
  }
  const packedText = JSON.stringify(packed)
  // Lines a list or an order filled in are not work yet: only a change from the start is.
  const [startText] = useState(packedText)
  const touched = packedText !== startText && draftHasWork(packed)
  // A draft from before, offered until it is continued or thrown away; meanwhile nothing
  // is saved over it.
  const [offer, setOffer] = useState(() =>
    keyOfDraft ? readDraft(keyOfDraft, parseReceiveDraft) : null,
  )
  const [dropped, setDropped] = useState(0)
  useEffect(() => {
    if (!keyOfDraft || offer) return
    const timer = window.setTimeout(() => {
      if (touched) saveDraft(keyOfDraft, JSON.parse(packedText) as ReceiveDraft)
      else clearDraft(keyOfDraft)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [keyOfDraft, offer, touched, packedText])
  const linesWord = (n: number) => `${n} line${n === 1 ? '' : 's'}`
  // X, Escape and Cancel close at once; the work stays as the draft.
  const leave = () => {
    if (keyOfDraft && !offer && touched) {
      saveDraft(keyOfDraft, packed)
      toast.info('Draft kept', {
        detail: `${linesWord(lines.length)} · open Receive stock again to go on`,
      })
    }
    onClose()
  }
  const continueDraft = async () => {
    if (!offer) return
    const found = new Map<string, ProductRow>()
    for (const id of new Set(offer.value.lines.map((l) => l.productId))) {
      const p = await repo.get('products', id)
      if (p && p.is_active && !p.deleted_at) found.set(id, p)
    }
    const due = order ? new Set(order.due.map((d) => d.lineId)) : null
    const restored = restoreLines(offer.value, found, due)
    if (!order) setSupplierId(offer.value.supplierId)
    setLocationId(offer.value.locationId)
    setReference(offer.value.reference)
    setBill({ ...offer.value.bill, method: offer.value.bill.method as BillDraft['method'] })
    setBillTyped(offer.value.billTyped)
    setLines(
      restored.lines.map(({ productId: _id, orderLineId, ...l }) => ({
        ...l,
        key: crypto.randomUUID(),
        orderLineId: orderLineId ?? undefined,
      })),
    )
    setDropped(restored.dropped)
    setOffer(null)
  }
  const startNew = () => {
    if (keyOfDraft) clearDraft(keyOfDraft)
    setOffer(null)
  }
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null
  const total = lines.reduce(
    (a, l) =>
      a +
      Math.round(
        (((l.product.track_serials
          ? parseSerials(l.serialsText).serials.length * 1000
          : l.qtyMilli) ?? 0) *
          (l.costPaisa ?? 0)) /
          1000,
      ),
    0,
  )
  const missingBatch = lines.some((l) => l.product.track_batches && !l.batchNo.trim())
  const missingExpiry = lines.some(
    (l) => l.product.track_batches && l.product.track_expiry && !l.expiryDate,
  )
  const qtyOf = (l: ReceiveLine) =>
    l.product.track_serials ? parseSerials(l.serialsText).serials.length * 1000 : l.qtyMilli
  const billDraft: BillDraft = billTyped ? bill : { ...bill, totalPaisa: total || null }
  const billProblem = supplierId ? billDraftProblem(billDraft) : null
  const invalid =
    lines.length === 0 ||
    lines.some((l) => !qtyOf(l)) ||
    !location ||
    missingBatch ||
    missingExpiry ||
    !!billProblem
  const batchesOf =
    useLive(
      async (r) => {
        const ids = [
          ...new Set(lines.filter((l) => l.product.track_batches).map((l) => l.product.id)),
        ]
        const out: Record<string, RowOf<'batches'>[]> = {}
        for (const id of ids) {
          out[id] = (await r.list('batches', { where: { product_id: id }, limit: 500 })).rows
        }
        return out
      },
      [lines.map((l) => l.product.id).join(',')],
      ['batches'],
    ).data ?? {}

  // Batch products can be received as several batches: each scan adds a line.
  const add = (product: ProductRow) =>
    setLines((ls) =>
      !product.track_batches && ls.some((l) => l.product.id === product.id)
        ? ls
        : [
            ...ls,
            {
              key: crypto.randomUUID(),
              product,
              qtyMilli: null,
              costPaisa: product.cost_price_paisa,
              batchNo: '',
              mfgDate: '',
              expiryDate: '',
              mrpPaisa: null,
              serialsText: '',
            },
          ],
    )
  const update = (i: number, patch: Partial<ReceiveLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)))
  // A known batch number fills in its dates; with a shelf life, expiry follows mfg.
  const onBatchNo = (i: number, value: string) => {
    const l = lines[i]
    if (!l) return
    const known = (batchesOf[l.product.id] ?? []).find(
      (b) => b.batch_no.trim().toLowerCase() === value.trim().toLowerCase(),
    )
    update(i, {
      batchNo: value,
      ...(known
        ? {
            mfgDate: known.mfg_date ?? '',
            expiryDate: known.expiry_date ?? '',
            expiryAuto: false,
            mrpPaisa: known.mrp_paisa,
          }
        : {}),
    })
  }
  const onMfg = (i: number, value: string) => {
    const l = lines[i]
    if (!l) return
    const next = followExpiry(
      value,
      { expiry: l.expiryDate, auto: l.expiryAuto ?? false },
      shelfOf(l.product),
    )
    update(i, { mfgDate: value, expiryDate: next.expiry, expiryAuto: next.auto })
  }

  const save = async () => {
    if (!current || !location) return
    // Serials: each one once, and not a unit that is in stock already.
    const serialLines = new Map<string, { serial: string; existing: SerialRow | null }[]>()
    const seen = new Set<string>()
    for (const l of lines) {
      if (!l.product.track_serials) continue
      const { serials, duplicates } = parseSerials(l.serialsText)
      const repeated = duplicates[0] ?? serials.find((x) => seen.has(`${l.product.id}:${x}`))
      if (repeated) return toast.error(`Serial ${repeated} is entered twice`)
      serials.forEach((x) => seen.add(`${l.product.id}:${x}`))
      const found = await loadSerials(repo, current.tenant_id, l.product.id, serials)
      for (const [serial, existing] of found) {
        const problem = receiveProblem(serial, existing)
        if (problem) return toast.error(`${l.product.name}: ${problem}`)
      }
      serialLines.set(
        l.key,
        [...found].map(([serial, existing]) => ({ serial, existing })),
      )
    }
    setBusy(true)
    try {
      const receiptId = crypto.randomUUID()
      const now = new Date().toISOString()
      // Goods that come before a generated order is sent mark it sent (docs/plans/po-ready.md).
      const stored = order ? await repo.get('purchase_orders', order.order.id) : null
      const markSent =
        stored?.status === 'issued'
          ? setPurchaseOrderStatus(
              { tenantId: current.tenant_id, now },
              purchaseOrderRow(stored),
              'sent',
            ).ops
          : []
      await repo.commit([
        ...markSent,
        ...receiveGoods(
          { tenantId: current.tenant_id },
          {
            receiptId,
            locationId: location,
            purchaseOrderId: order?.order.id ?? null,
            supplierId: supplierId || null,
            reference: reference.trim() || null,
            receiptDate: todayIso(),
            lines: lines.map((l) => ({
              productId: l.product.id,
              purchaseOrderLineId: l.orderLineId ?? null,
              qtyMilli: qtyOf(l)!,
              serials: serialLines.get(l.key),
              unitCostPaisa: l.costPaisa ?? 0,
              batch:
                l.product.track_batches && l.batchNo.trim()
                  ? {
                      batchNo: l.batchNo,
                      mfgDate: l.mfgDate || null,
                      expiryDate: l.expiryDate || null,
                      mrpPaisa: l.mrpPaisa,
                      existing:
                        (batchesOf[l.product.id] ?? []).find(
                          (b) => b.batch_no.trim().toLowerCase() === l.batchNo.trim().toLowerCase(),
                        ) ?? null,
                    }
                  : undefined,
            })),
          },
        ),
        // The supplier's bill and what was paid at the door, in the same commit.
        ...(supplierId
          ? billDraftOps({ tenantId: current.tenant_id, now }, billDraft, {
              supplierId,
              goodsReceiptId: receiptId,
              billNo: reference,
            })
          : []),
      ])
      if (keyOfDraft) clearDraft(keyOfDraft)
      toast.success(`Received ${lines.length} item${lines.length === 1 ? '' : 's'}`)
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={leave}
      title={order ? `Receive ${order.order.number ?? 'order'}` : 'Receive stock'}
      size="xl"
      unsaved={
        touched &&
        (lines.length > 0
          ? `${linesWord(lines.length)} not received yet`
          : 'The bill number is not saved yet')
      }
      footer={
        <>
          <span className="mr-auto self-center text-sm text-muted max-sm:basis-full">
            {supplierId && billDraft.totalPaisa
              ? `${lines.length} item${lines.length === 1 ? '' : 's'} · ${money(billDraft.paidPaisa ?? 0, settings)} paid, ${money(Math.max(0, billDraft.totalPaisa - (billDraft.paidPaisa ?? 0)), settings)} to pay`
              : `Total cost ${money(total, settings)}`}
            {missingBatch && ' · Enter a batch no. for each batch product'}
            {!missingBatch && missingExpiry && ' · Enter the expiry dates'}
            {billProblem && ` · ${billProblem}`}
          </span>
          <Button variant="secondary" onClick={leave}>
            Cancel
          </Button>
          <Button loading={busy} disabled={invalid || !!offer} onClick={() => void save()}>
            Receive
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {offer && (
          <DraftBanner
            savedAt={offer.savedAt}
            summary={`${linesWord(offer.value.lines.length)} · ${money(draftTotal(offer.value), settings)}`}
            onStartNew={startNew}
            onContinue={() => void continueDraft()}
          />
        )}
        <DroppedNote count={dropped} />
        <div inert={!!offer} className={cx('space-y-4', offer && 'opacity-50')}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={order ? 'Supplier' : 'Supplier (optional)'}>
              {(id) => (
                <SupplierPicker
                  id={id}
                  value={supplierId}
                  onChange={setSupplierId}
                  optional={!order}
                  disabled={!!order}
                />
              )}
            </Field>
            <Field label="Into">
              {(id) => (
                <LocationSelect
                  id={id}
                  locations={lookups.activeLocations}
                  value={location}
                  onChange={setLocationId}
                />
              )}
            </Field>
            <Field label="Supplier bill no. (optional)">
              {(id) => (
                <Input id={id} value={reference} onChange={(e) => setReference(e.target.value)} />
              )}
            </Field>
          </div>
          <ProductPicker
            onPick={add}
            placeholder={
              order
                ? 'Something came that was not on the order? Scan or search it…'
                : 'Scan or search products to receive…'
            }
            autoFocus={finePointer}
            addNew="product"
          />
          {lines.length > 0 && (
            <Table stack>
              <thead>
                <tr>
                  <TH>Product</TH>
                  <TH>Quantity</TH>
                  <TH>Unit cost</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  // On a phone: the product and its batch on a line, then quantity and cost
                  // side by side under their names.
                  <tr
                    key={l.key}
                    className="max-sm:grid-cols-[1fr_1fr_auto] max-sm:items-end max-sm:gap-x-2 max-sm:gap-y-2 max-sm:px-0"
                  >
                    <TD className="font-medium max-sm:col-span-full">
                      {l.product.name}
                      {l.product.track_serials && (
                        <div className="relative mt-2">
                          <Textarea
                            className="pr-11 font-mono"
                            rows={3}
                            aria-label={`Serial numbers of ${l.product.name}`}
                            placeholder="Serial / IMEI numbers, one per line (scan each)"
                            value={l.serialsText}
                            onChange={(e) => update(i, { serialsText: e.target.value })}
                          />
                          <ScanSerialsButton
                            text={l.serialsText}
                            onText={(serialsText) => update(i, { serialsText })}
                            label={`Scan serials for ${l.product.name}`}
                            className="absolute right-1 top-1"
                          />
                        </div>
                      )}
                      {l.product.track_batches && (
                        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.75fr)]">
                          <div className="relative max-sm:col-span-2">
                            <span className={BATCH_LABEL}>Batch no.</span>
                            <Input
                              aria-label={`Batch no. of ${l.product.name}`}
                              placeholder="Batch no."
                              list={`batches-${l.key}`}
                              value={l.batchNo}
                              onChange={(e) => onBatchNo(i, e.target.value)}
                              className="pr-10"
                            />
                            <ScanButton
                              mode="single"
                              title="Scan the batch number"
                              label={`Scan a batch number for ${l.product.name}`}
                              onCode={(code) => {
                                onBatchNo(i, code)
                                return { ok: true, message: code }
                              }}
                              className="absolute bottom-0.5 right-0.5"
                            />
                          </div>
                          <datalist id={`batches-${l.key}`}>
                            {(batchesOf[l.product.id] ?? []).map((b) => (
                              <option key={b.id} value={b.batch_no} />
                            ))}
                          </datalist>
                          {/* An empty date box says only dd/mm/yyyy: name it where there is no
                            mouse to hover. */}
                          <label className="block">
                            <span className={BATCH_LABEL}>Mfg date</span>
                            <Input
                              aria-label={`Mfg date of ${l.product.name}`}
                              type="date"
                              title="Mfg date"
                              value={l.mfgDate}
                              onChange={(e) => onMfg(i, e.target.value)}
                            />
                          </label>
                          {l.product.track_expiry ? (
                            <label className="block">
                              <span className={BATCH_LABEL}>
                                Expiry date
                                {l.expiryAuto && l.expiryDate && l.product.shelf_life_days && (
                                  <span
                                    className="inline-flex items-center gap-0.5 font-semibold text-primary"
                                    title="From the mfg date and the shelf life"
                                  >
                                    <Sparkles className="size-3.5" />
                                    {shelfLifeLabel(
                                      l.product.shelf_life_days,
                                      l.product.shelf_life_unit,
                                    )}
                                  </span>
                                )}
                              </span>
                              <Input
                                aria-label={`Expiry date of ${l.product.name}`}
                                type="date"
                                title="Expiry date"
                                value={l.expiryDate}
                                onChange={(e) =>
                                  update(i, { expiryDate: e.target.value, expiryAuto: false })
                                }
                              />
                            </label>
                          ) : (
                            <span className="max-sm:hidden" />
                          )}
                          <div className="max-sm:col-span-2">
                            <span className={BATCH_LABEL}>Batch MRP</span>
                            <MoneyInput
                              aria-label={`Batch MRP of ${l.product.name}`}
                              placeholder="Batch MRP"
                              paisa={l.mrpPaisa}
                              onValue={(p) => update(i, { mrpPaisa: p })}
                            />
                          </div>
                        </div>
                      )}
                    </TD>
                    <TD
                      className={cx(
                        'sm:w-24 max-sm:col-start-1 max-sm:col-end-2',
                        PHONE_LABEL,
                        "max-sm:before:content-['Quantity']",
                      )}
                    >
                      {l.product.track_serials ? (
                        <span className="tabular" aria-label={`Quantity of ${l.product.name}`}>
                          {qty(qtyOf(l) ?? 0)}
                        </span>
                      ) : (
                        <QtyInput
                          aria-label={`Quantity of ${l.product.name}`}
                          milli={l.qtyMilli}
                          allowsDecimal={lookups.unit(l.product.unit_id)?.allows_decimal ?? false}
                          onValue={(m) => update(i, { qtyMilli: m })}
                        />
                      )}
                    </TD>
                    <TD
                      className={cx(
                        'sm:w-28 max-sm:col-start-2 max-sm:col-end-3',
                        PHONE_LABEL,
                        "max-sm:before:content-['Unit_cost']",
                      )}
                    >
                      <MoneyInput
                        aria-label={`Cost of ${l.product.name}`}
                        paisa={l.costPaisa}
                        onValue={(p) => update(i, { costPaisa: p })}
                      />
                    </TD>
                    <TD className="sm:w-10 max-sm:col-start-3 max-sm:col-end-4">
                      <IconButton
                        label="Remove"
                        onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="size-4" />
                      </IconButton>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {supplierId && lines.length > 0 && (
            <section
              aria-label="Supplier’s bill"
              className="overflow-hidden rounded-xl border border-border"
            >
              <div className="flex items-center gap-2.5 border-b border-border bg-buy-wash px-3.5 py-2.5">
                <IconTile area="buy" size="sm">
                  <Receipt className="size-4" />
                </IconTile>
                <div>
                  <h3 className="text-sm font-semibold text-ink">Supplier’s bill</h3>
                  <p className="text-xs text-muted">
                    What they charged and what you paid now. Clear the total if they gave no bill.
                  </p>
                </div>
              </div>
              <div className="p-3.5">
                <BillFields
                  draft={billDraft}
                  onChange={(patch) => {
                    if ('totalPaisa' in patch) setBillTyped(true)
                    setBill((b) => ({ ...(billTyped ? b : billDraft), ...patch }))
                  }}
                  totalHint={
                    billTyped ? undefined : 'From the lines. Change it if the bill differs.'
                  }
                />
              </div>
            </section>
          )}
        </div>
      </div>
    </Modal>
  )
}
